/**
 * EditTemplateDialog - Edit custom permission template dialog
 * Extracted from SettingsPage to reduce file size
 */
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Edit } from "lucide-react";

const TEMPLATE_ICONS = ["👤", "🛡️", "⚕️", "📋", "💼", "🏥", "👨‍⚕️", "👩‍⚕️", "🔐", "📊"];
const TEMPLATE_COLORS = [
  { id: "emerald", class: "bg-emerald-100 border-emerald-300", label: "Emerald" },
  { id: "blue", class: "bg-blue-100 border-blue-300", label: "Blue" },
  { id: "purple", class: "bg-purple-100 border-purple-300", label: "Purple" },
  { id: "amber", class: "bg-amber-100 border-amber-300", label: "Amber" },
  { id: "pink", class: "bg-pink-100 border-pink-300", label: "Pink" },
  { id: "slate", class: "bg-slate-100 border-slate-300", label: "Slate" },
];

const PERMISSION_CATEGORIES = {
  clients: { label: "Clients", actions: ["view", "create", "edit", "delete"] },
  employees: { label: "Employees", actions: ["view", "create", "edit", "delete"] },
  schedule: { label: "Schedule", actions: ["view", "create", "edit", "delete"] },
  referrals: { label: "Referrals", actions: ["view", "create", "edit", "delete"] },
  billing: { label: "Billing", actions: ["view", "manage"] },
  reports: { label: "Reports", actions: ["view", "export"] },
};

export function EditTemplateDialog({
  editingTemplate,
  setEditingTemplate,
  saving,
  onSubmit,
  togglePermission,
}) {
  return (
    <Dialog open={!!editingTemplate} onOpenChange={(open) => !open && setEditingTemplate(null)}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit className="w-5 h-5 text-blue-600" />
            Edit Template: {editingTemplate?.name}
          </DialogTitle>
          <DialogDescription>
            Modify your custom permission template
          </DialogDescription>
        </DialogHeader>
        
        {editingTemplate && (
          <div className="space-y-6 py-4">
            {/* Template Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Template Name *</Label>
                <Input
                  placeholder="e.g., Senior Caregiver"
                  value={editingTemplate.name}
                  onChange={(e) => setEditingTemplate(prev => ({ ...prev, name: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Input
                  placeholder="Brief description of this template"
                  value={editingTemplate.description || ""}
                  onChange={(e) => setEditingTemplate(prev => ({ ...prev, description: e.target.value }))}
                />
              </div>
            </div>

            {/* Icon & Color Selection */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Icon</Label>
                <div className="flex flex-wrap gap-2 p-2 bg-slate-50 rounded-lg">
                  {TEMPLATE_ICONS.map(icon => (
                    <button
                      key={icon}
                      type="button"
                      onClick={() => setEditingTemplate(prev => ({ ...prev, icon }))}
                      className={`w-8 h-8 rounded flex items-center justify-center text-lg transition-all ${
                        editingTemplate.icon === icon 
                          ? 'bg-primary text-white shadow-md scale-110' 
                          : 'bg-white hover:bg-slate-100'
                      }`}
                    >
                      {icon}
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label>Color</Label>
                <div className="flex flex-wrap gap-2 p-2 bg-slate-50 rounded-lg">
                  {TEMPLATE_COLORS.map(color => (
                    <button
                      key={color.id}
                      type="button"
                      onClick={() => setEditingTemplate(prev => ({ ...prev, color: color.id }))}
                      className={`w-8 h-8 rounded border-2 transition-all ${color.class} ${
                        editingTemplate.color === color.id ? 'ring-2 ring-primary scale-110' : ''
                      }`}
                      title={color.label}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Permissions Grid */}
            <div className="space-y-2">
              <Label>Permissions</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 p-4 bg-slate-50 rounded-lg max-h-64 overflow-y-auto">
                {Object.entries(PERMISSION_CATEGORIES).map(([category, config]) => (
                  <div key={category} className="space-y-2">
                    <h5 className="font-medium text-sm text-slate-700">{config.label}</h5>
                    {config.actions.map(action => (
                      <div key={action} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id={`edit-${category}-${action}`}
                          checked={editingTemplate.permissions?.[category]?.includes(action) || false}
                          onChange={() => togglePermission(category, action, true)}
                          className="rounded border-slate-300"
                        />
                        <label 
                          htmlFor={`edit-${category}-${action}`}
                          className="text-xs text-slate-600 capitalize cursor-pointer"
                        >
                          {action.replace("_", " ")}
                        </label>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => setEditingTemplate(null)}>
            Cancel
          </Button>
          <Button 
            onClick={onSubmit} 
            disabled={saving}
            data-testid="update-template-btn"
          >
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
